/**
 * Offline Queue Service
 * Stores actions in IndexedDB when offline, syncs when back online
 * Critical for time clock punches, availability updates, etc.
 */

import {
  queuePunch, listPunches, pendingCount as timeclockPendingCount, syncPunches as syncTimeclockPunches,
  cleanup as cleanupTimeclock,
} from '@/lib/timeclock/punchQueue';

const DB_NAME = 'asterpos-offline';
const DB_VERSION = 1;
const STORES = {
  ACTIONS: 'offline-actions',
  TIMECLOCK: 'timeclock-punches',
  SYNC_LOG: 'sync-log'
};

// Open/create IndexedDB
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // General offline actions queue
      if (!db.objectStoreNames.contains(STORES.ACTIONS)) {
        const actionsStore = db.createObjectStore(STORES.ACTIONS, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        actionsStore.createIndex('type', 'type', { unique: false });
        actionsStore.createIndex('createdAt', 'createdAt', { unique: false });
        actionsStore.createIndex('status', 'status', { unique: false });
      }
      
      // Dedicated time clock punches store
      if (!db.objectStoreNames.contains(STORES.TIMECLOCK)) {
        const timeclockStore = db.createObjectStore(STORES.TIMECLOCK, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        timeclockStore.createIndex('staffId', 'staffId', { unique: false });
        timeclockStore.createIndex('punchType', 'punchType', { unique: false });
        timeclockStore.createIndex('synced', 'synced', { unique: false });
        timeclockStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      // Sync log for debugging
      if (!db.objectStoreNames.contains(STORES.SYNC_LOG)) {
        const logStore = db.createObjectStore(STORES.SYNC_LOG, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        logStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

// ==================== GENERAL OFFLINE ACTIONS ====================

/**
 * Queue an action for later sync
 * @param {Object} action - { type, endpoint, method, data, token }
 */
export async function queueAction(action) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.ACTIONS, 'readwrite');
    const store = tx.objectStore(STORES.ACTIONS);
    
    const record = {
      ...action,
      createdAt: Date.now(),
      status: 'pending',
      retryCount: 0
    };
    
    const request = store.add(record);
    request.onsuccess = () => {
      console.log('[OfflineQueue] Action queued:', action.type);
      resolve(request.result);
      // Request background sync if supported
      requestBackgroundSync('sync-actions');
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all pending actions
 */
export async function getPendingActions() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.ACTIONS, 'readonly');
    const store = tx.objectStore(STORES.ACTIONS);
    const index = store.index('status');
    const request = index.getAll('pending');
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Mark action as synced
 */
export async function markActionSynced(id, response = null) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.ACTIONS, 'readwrite');
    const store = tx.objectStore(STORES.ACTIONS);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        record.status = 'synced';
        record.syncedAt = Date.now();
        record.response = response;
        store.put(record);
      }
      resolve();
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Mark action as failed
 */
export async function markActionFailed(id, error) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.ACTIONS, 'readwrite');
    const store = tx.objectStore(STORES.ACTIONS);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        record.retryCount += 1;
        record.lastError = error;
        record.lastRetryAt = Date.now();
        // Mark as failed after 5 retries
        if (record.retryCount >= 5) {
          record.status = 'failed';
        }
        store.put(record);
      }
      resolve();
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// ==================== TIME CLOCK SPECIFIC ====================
// The time clock's offline punches live in the rebuilt queue (lib/timeclock/punchQueue:
// device time + client punch id, PIN sealed with a device key, sent to
// /api/timeclock/v2/punches/sync, idempotent). These names stay for older callers.

/**
 * Save a time clock punch on the device.
 * @param {Object} punch - { auth ('account' | 'backoffice' | 'staff'), restaurantId, staffId,
 *   staffName, phone, pin, action, breakPaid, roleId }
 */
export async function saveTimeclockPunch(punch) {
  const p = punch || {};
  return queuePunch({
    auth: p.auth || 'staff', restaurantId: p.restaurantId, action: String(p.action || '').toUpperCase(),
    breakPaid: !!p.breakPaid, roleId: p.roleId || null,
    who: { staffId: p.staffId ?? null, staffName: p.staffName || null, phone: p.phone || null, pin: p.pin || null },
  });
}

/** The punches still waiting on this device. */
export async function getUnsyncedPunches() {
  return (await listPunches()).filter((r) => r.status === 'pending');
}

export async function getTodayPunchCount(staffId) {
  const today = new Date().toDateString();
  return (await listPunches()).filter((r) => r.staffId === staffId && new Date(r.occurredAtMs).toDateString() === today).length;
}

// ==================== SYNC UTILITIES ====================

/**
 * Request background sync (if supported)
 */
function requestBackgroundSync(tag) {
  if ('serviceWorker' in navigator && 'sync' in window.SyncManager) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.sync.register(tag).catch(err => {
        console.log('[OfflineQueue] Background sync not available:', err);
      });
    });
  }
}

/**
 * Manually trigger sync (call this when coming back online)
 */
export async function syncAll() {
  const results = {
    actions: { success: 0, failed: 0 },
    timeclock: { success: 0, failed: 0 }
  };
  
  // Sync general actions
  const pendingActions = await getPendingActions();
  for (const action of pendingActions) {
    try {
      const response = await fetch(action.endpoint, {
        method: action.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(action.token && { 'Authorization': `Bearer ${action.token}` })
        },
        body: JSON.stringify(action.data)
      });
      
      if (response.ok) {
        await markActionSynced(action.id, await response.json());
        results.actions.success++;
      } else {
        await markActionFailed(action.id, `HTTP ${response.status}`);
        results.actions.failed++;
      }
    } catch (error) {
      await markActionFailed(action.id, error.message);
      results.actions.failed++;
    }
  }
  
  // Time clock punches: the rebuilt queue, each with its own session's headers
  try {
    const tc = await syncTimeclockPunches();
    results.timeclock.success = tc.applied + tc.review + tc.rejected;
    results.timeclock.failed = tc.retry + tc.waiting;
  } catch (error) {
    results.timeclock.failed++;
  }

  console.log('[OfflineQueue] Sync complete:', results);
  return results;
}

/**
 * Add to sync log
 */
export async function logSync(message, data = {}) {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORES.SYNC_LOG, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_LOG);
    store.add({
      message,
      data,
      timestamp: Date.now()
    });
    resolve();
  });
}

/**
 * Get sync statistics
 */
export async function getSyncStats() {
  const [pendingActions, unsyncedPunches] = await Promise.all([
    getPendingActions().catch(() => []),
    timeclockPendingCount()
  ]);

  return {
    pendingActions: pendingActions.length,
    unsyncedPunches,
    total: pendingActions.length + unsyncedPunches
  };
}

// ==================== CLEANUP ====================

/**
 * Clear synced records older than 7 days
 */
export async function cleanupOldRecords() {
  const db = await openDB();
  const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
  
  // Clean actions
  const actionsTx = db.transaction(STORES.ACTIONS, 'readwrite');
  const actionsStore = actionsTx.objectStore(STORES.ACTIONS);
  const actionsIndex = actionsStore.index('createdAt');
  const actionsRange = IDBKeyRange.upperBound(sevenDaysAgo);
  actionsIndex.openCursor(actionsRange).onsuccess = (event) => {
    const cursor = event.target.result;
    if (cursor) {
      if (cursor.value.status === 'synced') {
        cursor.delete();
      }
      cursor.continue();
    }
  };
  
  // Time clock punches clean themselves up (lib/timeclock/punchQueue)
  await cleanupTimeclock();

  console.log('[OfflineQueue] Cleaned up old records');
}

export default {
  queueAction,
  getPendingActions,
  markActionSynced,
  markActionFailed,
  saveTimeclockPunch,
  getUnsyncedPunches,
  getTodayPunchCount,
  syncAll,
  getSyncStats,
  cleanupOldRecords
};
